import type { Configuration } from "rollbar";
import Rollbar from "rollbar";
import { ROLLBAR_ACCESS_TOKEN, ENV, ENABLE_ROLLBAR } from "./config";
import { getTenant } from "./helpers/tenant";

// Get version hash if available
const getCodeVersion = async (): Promise<string> => {
  try {
    const response = await fetch("/version.json");
    const data = await response.json();
    return data.version || "unknown";
  } catch {
    return "unknown";
  }
};

export const rollbarConfig: Configuration = {
  accessToken: ROLLBAR_ACCESS_TOKEN,
  enabled: ENABLE_ROLLBAR && !!ROLLBAR_ACCESS_TOKEN,
  captureUncaught: true,
  captureUnhandledRejections: true,
  payload: {
    environment: ENV,
    client: {
      javascript: {
        code_version: "1.0.0", // Will be updated dynamically
        source_map_enabled: true,
        guess_uncaught_frames: true,
      },
    },
  },
  // Transform payload to add custom data
  transform: (payload) => {
    // Add tenant information if available
    const tenant = getTenant();
    if (tenant) {
      payload.context = tenant;
    }
  },
  // Filter out sensitive data
  scrubFields: [
    "password",
    "confirm_password",
    "password_confirmation",
    "secret",
    "access_token",
    "api_key",
    "apiKey",
    "auth",
    "authentication",
    "authorization",
    "bearer",
    "cookie",
    "csrf",
    "token",
  ],
  // Group errors by removing dynamic parts from URLs
  autoInstrument: {
    network: true,
    log: false,
    dom: true,
    navigation: true,
    connectivity: true,
  },
};

// Initialize code version
getCodeVersion().then((version) => {
  if (rollbarConfig.payload?.client?.javascript) {
    rollbarConfig.payload.client.javascript.code_version = version;
  }
});

/**
 * Initialize Rollbar error tracking
 * Should be called once at application startup
 */
export const initializeRollbar = (): void => {
  if (ENABLE_ROLLBAR) {
    const rollbarInstance = new Rollbar(rollbarConfig);
    window.Rollbar = rollbarInstance;
  }
};
